import React, { useState, useEffect, useMemo } from 'react';
import { Popover, Button } from '@mui/material';
import TagSelect from '../TagSelect/TagSelect';
import { Guid } from '@Shared.Angular/@types/guid';
import TagGroupApiService from '@Shared.Angular/flowingly.services/taggroup.api.service';
import { useService } from '@Shared.Angular/flowingly.hooks/useService';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { SharedAngular } from '@Angular.Runner/@types/sharedAngular';

interface Props {
  tagsUsed: Guid[];
  onApply: (selectedTags: Guid[]) => void;
}

const TagFilter: React.FC<Props> = ({ tagsUsed, onApply }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTags, setSelectedTags] = useState<Guid[]>([]);
  const [tempSelectedTags, setTempSelectedTags] = useState<Guid[]>([]);
  const [tagGroups, setTagGroups] = useState<ITagGroupDetail[]>([]);

  const { enableTagging } = useService<SharedAngular.APP_CONFIG>('APP_CONFIG');
  const tagGroupApiService =
    useService<TagGroupApiService>('tagGroupApiService');

  const tagsUsedSet = useMemo(() => new Set(tagsUsed), [tagsUsed]);

  useEffect(() => {
    fetchTags();
  }, [tagsUsedSet]);

  const fetchTags = async () => {
    const tagGroups = await tagGroupApiService.getAll();

    const filteredTagGroups = tagGroups
      .map((group) => ({
        ...group,
        tags: group.tags.filter((tag) => tagsUsedSet.has(tag.id))
      }))
      .filter((group) => group.tags.length > 0);

    setTagGroups(filteredTagGroups);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setTempSelectedTags(selectedTags);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    setSelectedTags(tempSelectedTags);
    onApply(tempSelectedTags);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'filter-tag-popover' : undefined;
  const count = selectedTags.length;
  const label = count > 0 ? 'Selected' : 'Find Tags';

  if (!enableTagging) return null;

  return (
    <>
      <div id={id} className="tag-filter-trigger" onClick={handleClick}>
        {count > 0 && <span className="tag-count">{count}</span>}
        <span className="tag-label">{label}</span>
        <i className="fa fa-chevron-down tag-icon" />
      </div>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <div className="tag-filter-popover-container">
          <TagSelect
            allTagGroups={tagGroups}
            selectedTags={tempSelectedTags}
            onChange={(newTags: Guid[]) => setTempSelectedTags(newTags)}
          />

          <div className="tag-filter-actions">
            <Button className="cancel-button" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              className="apply-button"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default TagFilter;
